import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { OptionsButton } from "./OptionsButton.tsx";
import { loc, type Loc } from "./loc.ts";
import { useGt } from "./gameText.ts";
import { useLang } from "../../gamzone/i18n.ts";

/** One character cutout placed over an ending cut — its own position (left/
 *  right/height, whatever pins it in frame) and an optional stagger delay so
 *  several overlays on the same cut fade in one after another, not at once. */
export interface EndingOverlay {
  src: string;
  style: CSSProperties;
  delayMs?: number;
}

/** One background "cut" of the campaign ending: its own bg image, one or
 *  more narration lines shown while that cut is up, and optional overlay
 *  images (character cutouts) that fade in for the whole cut. */
export interface EndingCut {
  bg: string;
  lines: readonly Loc[];
  overlays?: readonly EndingOverlay[];
}

/**
 * The campaign ending — like `Subtitles`, but the background (and optional
 * overlay image) changes per "cut" as the narration moves through it, not a
 * single static bg for the whole scene. Tap / click / Enter advances one
 * line at a time; the LAST line is a true dead end — no button, no further
 * tap — the ending just stays on screen (test-build "다시 시작"/"처음으로" in
 * the right panel are still there for anyone who wants to replay).
 */
export function Ending({ cuts }: { cuts: readonly EndingCut[] }) {
  const lang = useLang();
  const gt = useGt();
  // Flatten every cut's lines into one sequence, each entry remembering
  // which cut (and therefore which bg/overlay) it belongs to.
  const flat = useMemo(
    () =>
      cuts.flatMap((cut, cutIndex) =>
        cut.lines.map((text) => ({ cutIndex, text })),
      ),
    [cuts],
  );
  const [i, setI] = useState(0);
  const last = flat.length - 1;
  const atLast = i >= last;
  const cutIndex = flat[i]?.cutIndex ?? 0;
  const cut = cuts[cutIndex];

  const advance = useCallback(() => {
    setI((n) => (n >= last ? n : n + 1));
  }, [last]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        advance();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [advance]);

  return (
    <div
      className="cam-screen cam-prologue cam-ending"
      style={{
        ["--cam-bg" as string]: `url(${cut.bg})`,
        cursor: atLast ? "default" : "pointer",
      }}
      role={atLast ? undefined : "button"}
      tabIndex={atLast ? undefined : 0}
      aria-label={atLast ? undefined : gt("nextLine")}
      onClick={atLast ? undefined : advance}
    >
      <OptionsButton />

      {cut.overlays?.map((o) => (
        // The positioning/flip transform lives on this static wrapper, kept
        // separate from the fade-in animation below — both would otherwise
        // fight over the `transform` property and the flip would only snap
        // in once the animation finished (a visible flicker on mount).
        <div key={o.src} className="cam-ending-overlay-slot" style={o.style}>
          <img
            className="cam-ending-overlay"
            src={o.src}
            alt=""
            aria-hidden
            style={{ animationDelay: `${o.delayMs ?? 0}ms` }}
          />
        </div>
      ))}
      <div className="cam-subtitle">
        <p key={i} className="cam-subtitle-line">
          {flat[i] ? loc(flat[i].text, lang) : ""}
        </p>
        {flat.length > 1 && (
          <div className="cam-progress" aria-hidden>
            {flat.map((_, n) => (
              <span key={n} className={n <= i ? "on" : ""} />
            ))}
          </div>
        )}
        {atLast ? (
          <span className="cam-ending-thanks">{gt("thanksForPlaying")}</span>
        ) : (
          <span className="cam-hint">{gt("tapContinue")}</span>
        )}
      </div>
    </div>
  );
}
